// Player history
// This will go through all the leagues/teams that a player used to play for
// and show all their stats.
// UPDATE: also shows videos for this player
import React, { useState, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import './Players.css'
import ErrorModal from '../../shared/components/UIElements/ErrorModal'
import LoadingSpinner from '../../shared/components/UIElements/LoadingSpinner'
import Footer from '../../shared/components/UIElements/Footer'
import PlayerHistoryHome from '../../shared/components/Navigation/PlayerHistoryHome'
import YouTube from 'react-youtube'
var getYouTubeID = require('get-youtube-id')

const PlayerHistory = () => {
	const [error, setError] = useState()
	const [isLoading, setIsLoading] = useState(false)

	const [loadedRosters, setLoadedRosters] = useState()
	const [firstName, setFirstName] = useState()
	const [lastName, setLastName] = useState()
	const [middleInitial, setMiddleInitial] = useState()
	const [number, setNumber] = useState()
	const [photo, setPhoto] = useState()
	const [listOfVideos, setListOfVideos] = useState()

	const [onlyBeenGoalie, setOnlyBeenGoalie] = useState()
	const [hasPlayedOutAndBeenGoalie, setHasPlayedOutAndBeenGoalie] = useState()
	const [neverPlayedGoalie, setNeverPlayedGoalie] = useState()
	const [processDone, setProcessDone] = useState(false)

	const [goalieStats, setGoalieStats] = useState(false)
	const [goalieStatsByRosterSeason, setGoalieStatsByRosterSeason] = useState()
	const [goalieAndNonGoalieStats, setGoalieAndNonGoalieStats] = useState()
	const [seasonsWherePlayerWasGoalie, setSeasonsWherePlayerWasGoalie] =
		useState()
	const [seasonsWherePlayerWasNotGoalie, setSeasonsWherePlayerWasNotGoalie] =
		useState()
	const [isMobile440, setIsMobile440] = useState(false)

	const playerId = useParams().playerId

	// Derive videoIds directly from listOfVideos (no sticky state)
	const videoIds = useMemo(() => {
		if (!listOfVideos?.length) return []
		return listOfVideos.map((v) => getYouTubeID(v.videoURL))
	}, [listOfVideos])

	//
	// Fetch history on playerId change (ONLY dependency should be playerId)
	//
	useEffect(() => {
		const sendRequest = async () => {
			setIsLoading(true)
			setError(null)

			// reset per-player computed flags/states so they don't carry over
			setLoadedRosters(undefined)
			setFirstName(undefined)
			setLastName(undefined)
			setMiddleInitial(undefined)
			setNumber(undefined)
			setPhoto(undefined)
			setListOfVideos(undefined)

			setOnlyBeenGoalie(undefined)
			setHasPlayedOutAndBeenGoalie(undefined)
			setNeverPlayedGoalie(undefined)
			setProcessDone(false)

			setGoalieStats(false)
			setGoalieStatsByRosterSeason(undefined)
			setGoalieAndNonGoalieStats(undefined)
			setSeasonsWherePlayerWasGoalie(undefined)
			setSeasonsWherePlayerWasNotGoalie(undefined)

			try {
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/player/${playerId}/history`,
				)

				const responseData = await response.json()

				if (!response.ok) {
					throw new Error(responseData.message)
				}

				setLoadedRosters(responseData.listOfRosters)
				setFirstName(responseData.firstName)
				setLastName(responseData.lastName)
				setMiddleInitial(responseData.middleInitial)
				setNumber(responseData.number)
				setPhoto(responseData.photo)
				setListOfVideos(responseData.listOfVideos)
			} catch (err) {
				setError(err.message)
			} finally {
				setIsLoading(false)
			}
		}

		sendRequest()
	}, [playerId])

	//
	// Determine goalie/non-goalie path ONCE per player
	//
	useEffect(() => {
		if (!loadedRosters || processDone) return

		let wasGoalie = 0
		let wasOut = 0

		loadedRosters.forEach((roster) => {
			if (roster.isGoalie) wasGoalie++
			else wasOut++
		})

		if (wasGoalie > 0 && wasOut === 0) setOnlyBeenGoalie(true)
		if (wasGoalie > 0 && wasOut > 0) setHasPlayedOutAndBeenGoalie(true)
		if (wasGoalie === 0 && wasOut > 0) setNeverPlayedGoalie(true)

		setProcessDone(true)
	}, [loadedRosters, processDone])
	//
	//
	useEffect(() => {
		const checkMobile = () => {
			setIsMobile440(window.matchMedia('(max-width: 440px)').matches)
		}

		checkMobile()

		window.addEventListener('resize', checkMobile)

		return () => {
			window.removeEventListener('resize', checkMobile)
		}
	}, [])
	//
	// player-controller, getGoalieStats
	//
	const getGoalieStats = async (playerId) => {
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BACKEND_URL}/player/goalieStats/${playerId}`,
			)
			const responseData = await response.json()
			if (!response.ok) throw new Error(responseData.message)

			setGoalieStatsByRosterSeason(responseData.goalieStatsByRosterSeason)
		} catch (err) {
			setError(err.message)
		}
		setGoalieStats(true)
	}

	//
	// player-controller, getGoalieAndNonGoalieStats
	//
	const getGoalieAndNonGoalieStats = async (playerId) => {
		try {
			const response = await fetch(
				`${process.env.REACT_APP_BACKEND_URL}/player/goalieAndNonGoalieStats/${playerId}`,
			)
			const responseData = await response.json()
			if (!response.ok) throw new Error(responseData.message)

			setSeasonsWherePlayerWasGoalie(responseData.seasonsWherePlayerWasGoalie)
			setSeasonsWherePlayerWasNotGoalie(
				responseData.seasonsWherePlayerWasNotGoalie,
			)
		} catch (err) {
			setError(err.message)
		}
		setGoalieAndNonGoalieStats(true)
	}

	//
	// Kick off stats fetches only after we know which path, and only once per player
	//
	useEffect(() => {
		if (!processDone) return
		if (goalieStats) return

		if (onlyBeenGoalie) {
			getGoalieStats(playerId)
			return
		}

		if (hasPlayedOutAndBeenGoalie && !goalieAndNonGoalieStats) {
			getGoalieAndNonGoalieStats(playerId)
			return
		}
	}, [
		processDone,
		goalieStats,
		goalieAndNonGoalieStats,
		onlyBeenGoalie,
		hasPlayedOutAndBeenGoalie,
		playerId,
	])

	const errorHandler = () => setError(null)

	let rem
	if (loadedRosters) {
		rem = loadedRosters.length > 10 ? '2rem' : '6rem'
	}

	const fullName = `${firstName} ${lastName}`

	const nameLength = fullName.replace(/\s/g, '').length

	let playerNameClass = 'playerHeroName'

	if (nameLength > 18) {
		playerNameClass += ' playerHeroNameMedium'
	}

	if (nameLength > 24) {
		playerNameClass += ' playerHeroNameSmall'
	}

	return (
		<div style={{ overflowX: 'hidden' }}>
			<React.Fragment>
				<PlayerHistoryHome />
				<ErrorModal error={error} onClear={errorHandler} />

				{isLoading && (
					<div className='center' style={{ marginTop: '6rem' }}>
						<LoadingSpinner />
					</div>
				)}

				{!isLoading && loadedRosters && (
					<div className='Roster' style={{ marginTop: '2rem' }}>
						<div
							className={`playerHero ${
								!photo || photo === '' ? 'playerHeroNoPhoto' : ''
							}`}
						>
							{photo && (
								<div className='playerHeroPhotoWrap'>
									<img
										src={`${process.env.REACT_APP_AWS_BUCKET}/${photo}`}
										className='playerHeroPhoto'
										alt='player'
									/>
								</div>
							)}

							<div className='playerHeroInfo'>
								<div className='playerHeroNumber'>#{number}</div>

								<h1
									className={`${playerNameClass} ${
										nameLength > 18 ? 'playerHeroNameCondensed' : ''
									}`}
								>
									{fullName}
								</h1>

								<div className='playerHeroDivider'>
									<span></span>
									<div>★</div>
									<span></span>
								</div>

								<h3 className='playerHeroHistory'>Player History</h3>

								<p className='playerHeroLegend'>
									(PG: Playoff Goals | PA: Playoff Assists | GWG: Game-Winning
									Goals)
								</p>
							</div>
						</div>
						{onlyBeenGoalie && (
							<table id='roster'>
								<thead>
									<tr>
										<th>Team</th>
										<th>Year</th>
										<th>#</th>
										<th>GP</th>
										<th>W</th>
										<th>L</th>
										<th>Ties</th>
										<th>SO</th>
										<th>GAA</th>
									</tr>
								</thead>
								<tbody>
									{loadedRosters.map((team, key) => (
										<tr key={key}>
											<td>
												<Link
													style={{
														textDecoration: 'none',
														fontWeight: 600,
														color: 'black',
													}}
													to={`/league/${team.leagueId}/${team.divisionName}/${team.year}/${team.teamName}`}
												>
													{team.divisionName} -{' '}
													{isMobile440 && team.abbreviation
														? team.abbreviation
														: team.teamName}
												</Link>
											</td>
											<td>{team.year}</td>
											<td>{team.number}</td>
											<td>{team.goalieGamesPlayed}</td>
											<td>{team.goalieWins}</td>
											<td>{team.goalieLosses}</td>
											<td>{team.goalieTies}</td>
											<td>{team.goalieShutouts}</td>
											<td>
												{!isNaN(
													Number(
														team.goalsAgainst / team.goalieGamesPlayed,
													).toFixed(3),
												) &&
													Number(
														team.goalsAgainst / team.goalieGamesPlayed,
													).toFixed(3)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						)}

						{neverPlayedGoalie && (
							<table id='roster'>
								<thead>
									<tr>
										<th>Team</th>
										<th>Year</th>
										<th>#</th>
										<th>G</th>
										<th>A</th>
										<th>PG</th>
										<th>PA</th>
										<th>GWG</th>
										<th>Total</th>
										<th>PIM</th>
									</tr>
								</thead>
								<tbody>
									{loadedRosters.map((team, key) => (
										<tr key={key}>
											<td>
												<Link
													style={{
														textDecoration: 'none',
														fontWeight: 600,
														color: 'black',
													}}
													to={`/league/${team.leagueId}/${team.divisionName}/${team.year}/${team.teamName}`}
												>
													{team.divisionName} -{' '}
													{isMobile440 && team.abbreviation
														? team.abbreviation
														: team.teamName}
												</Link>
											</td>
											<td>{team.year}</td>
											<td>{team.number}</td>
											<td>{team.goals}</td>
											<td>{team.assists}</td>
											<td>{team.playoffGoals}</td>
											<td>{team.playoffAssists}</td>
											<td>{team.gwg}</td>
											<td>
												{team.goals +
													team.assists +
													team.playoffGoals +
													team.playoffAssists}
											</td>
											<td>{team.pim}</td>
										</tr>
									))}
								</tbody>
							</table>
						)}

						{hasPlayedOutAndBeenGoalie && (
							<React.Fragment>
								<table id='roster'>
									<thead>
										<tr>
											<th>Team</th>
											<th>Year</th>
											<th>#</th>
											<th>G</th>
											<th>A</th>
											<th>PG</th>
											<th>PA</th>
											<th>Total</th>
											<th>PIM</th>
											<th>W</th>
											<th>L</th>
											<th>Ties</th>
											<th>SO</th>
											<th>GAA</th>
										</tr>
									</thead>
									<tbody>
										{loadedRosters.map((team, key) => (
											<tr key={key}>
												<td>
													<Link
														style={{
															textDecoration: 'none',
															fontWeight: 600,
															color: 'black',
														}}
														to={`/league/${team.leagueId}/${team.divisionName}/${team.year}/${team.teamName}`}
													>
														{team.divisionName} -{' '}
														{isMobile440 && team.abbreviation
															? team.abbreviation
															: team.teamName}
													</Link>
													{team.isGoalie ? ' (G)' : ''}
												</td>
												<td>{team.year}</td>
												<td>{team.number}</td>
												<td>{team.goals}</td>
												<td>{team.assists}</td>
												<td>{team.playoffGoals}</td>
												<td>{team.playoffAssists}</td>
												<td>
													{team.goals +
														team.assists +
														team.playoffGoals +
														team.playoffAssists}
												</td>
												<td>{team.pim}</td>
												<td>{team.goalieWins}</td>
												<td>{team.goalieLosses}</td>
												<td>{team.goalieTies}</td>
												<td>{team.goalieShutouts}</td>
												<td>
													{!isNaN(
														Number(
															team.goalsAgainst / team.goalieGamesPlayed,
														).toFixed(3),
													) &&
														Number(
															team.goalsAgainst / team.goalieGamesPlayed,
														).toFixed(3)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</React.Fragment>
						)}

						{/* Videos */}
						{listOfVideos?.length > 0 && (
							<div className='section pt-5 playerVideos'>
								<div>
									{/* Existing desktop/tablet heading */}
									<div className='playerVideosDesktopHeading'>
										<div className='row'>
											<div className='col text-center mb-0'>
												<h1 className='text-sh-dark display-2'>
													Check it out!
												</h1>
											</div>
										</div>
									</div>

									{/* New mobile-only heading */}
									<div className='playerHistoryVideoHeading'>
										<div className='playerHistoryVideoHeading__top'>
											<span className='playerHistoryVideoHeading__line'></span>

											<span className='playerHistoryVideoHeading__icon'>
												<span className='playerHistoryVideoHeading__screen'>
													<span className='playerHistoryVideoHeading__play'></span>
												</span>
											</span>

											<span className='playerHistoryVideoHeading__line'></span>
										</div>

										<h1>Check it out!</h1>
									</div>

									<div className='row playerHistoryVideosRow'>
										<div className='col-lg-3 col-sm-3'></div>

										<div className='col-lg-6 col-sm-6 playerHistoryVideosColumn'>
											{listOfVideos.map((video, key) => (
												<div
													key={`${playerId}-${key}`}
													className='playerHistoryVideo'
												>
													<h1 className='playerHistoryVideo__title'>
														{video.videoTitle}
													</h1>

													<p className='caption playerHistoryVideo__caption'>
														{video.videoCaption}
													</p>

													<div className='playerHistoryVideo__embed'>
														<YouTube videoId={videoIds[key]} />
													</div>
												</div>
											))}
										</div>

										<div className='col-lg-3 col-sm-3'></div>
									</div>
								</div>
							</div>
						)}
					</div>
				)}

				<div style={{ marginTop: `${rem}` }}>
					<Footer />
				</div>
			</React.Fragment>
		</div>
	)
}

export default PlayerHistory
